import { useEffect, useState } from 'react'

/**
 * Single source of truth for admin sidebar geometry.
 *
 * Previously Admin.jsx and AdminNavbar.jsx each hard-coded their own widths and
 * disagreed below 975px (container reserved 130px, the fixed sidebar painted
 * 200px, and main content offset by 40px), which left ~160px of page content
 * underneath the sidebar. Every consumer now derives from these constants.
 */
export const SIDEBAR_EXPANDED_WIDTH = 260
export const SIDEBAR_RAIL_WIDTH = 72
export const SIDEBAR_DRAWER_WIDTH = 280
export const MOBILE_TOPBAR_HEIGHT = 56

/**
 * Below this width the sidebar stops reserving layout space and becomes an
 * overlay drawer. The same value drives both the CSS media queries and the JS
 * that decides what the toggle does, so the two can't drift apart.
 */
export const SIDEBAR_BREAKPOINT = 975

export const BELOW_SIDEBAR_BREAKPOINT = `(max-width: ${SIDEBAR_BREAKPOINT}px)`

/** Hover-to-peek is an enhancement, never the only way to change state. */
export const HOVER_CAPABLE = '(hover: hover) and (pointer: fine)'

export const SIDEBAR_MODE_STORAGE_KEY = 'focal-admin-sidebar-mode'

export const SIDEBAR_EXPANDED = 'expanded'
export const SIDEBAR_COLLAPSED = 'collapsed'

/**
 * Reactive media query hook.
 *
 * This replaces reading `window.innerWidth` inside styled-components template
 * literals, which only evaluated at render time and never updated on resize.
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined

    const mediaQueryList = window.matchMedia(query)
    const handleChange = (event) => setMatches(event.matches)

    // Re-sync in case the viewport changed between the initial render and now.
    setMatches(mediaQueryList.matches)

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handleChange)
      return () => mediaQueryList.removeEventListener('change', handleChange)
    }

    // Safari < 14 only exposes the deprecated listener API.
    mediaQueryList.addListener(handleChange)
    return () => mediaQueryList.removeListener(handleChange)
  }, [query])

  return matches
}

/* The screens that hide the sidebar: the editors, and the record pages that open in a tab of their
   own. Shared by Admin.jsx and its loading outline, so the outline draws the frame the screen will. */
const SIDEBARLESS = [
  'packages-editor',
  'booking/new',
  'edit-booking',
  'preview',
  'gallery/edit',
  'blog/blog-editor',
  'page/page-editor',
  'creator/edit',
]
export const hidesSidebar = (pathname = '') => SIDEBARLESS.some((part) => pathname.includes(part))

export function readStoredSidebarMode() {
  try {
    const stored = window.localStorage.getItem(SIDEBAR_MODE_STORAGE_KEY)
    return stored === SIDEBAR_COLLAPSED || stored === SIDEBAR_EXPANDED ? stored : SIDEBAR_EXPANDED
  } catch {
    // Storage can throw in private browsing modes. Fall back to the default.
    return SIDEBAR_EXPANDED
  }
}

export function storeSidebarMode(mode) {
  try {
    window.localStorage.setItem(SIDEBAR_MODE_STORAGE_KEY, mode)
  } catch {
    // Non-fatal: the sidebar still works, it just won't remember the choice.
  }
}
